import { memo } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { ComponentType } from 'react'

/** Props for BottomNav component */
interface BottomNavProps {
  isAdmin?: boolean
  pendingCount?: number
}

/** Props for icon components */
interface IconProps {
  className?: string
}

/** Navigation link configuration */
interface NavLinkConfig {
  to: string
  labelKey: string
  icon: ComponentType<IconProps>
  showBadge?: boolean
}

const BottomNav = memo(function BottomNav({ isAdmin = false, pendingCount = 0 }: BottomNavProps): JSX.Element {
  const { t } = useTranslation()
  const location = useLocation()

  const employeeLinks: NavLinkConfig[] = [
    {
      to: '/dashboard',
      labelKey: 'nav.home',
      icon: HomeIcon
    },
    {
      to: '/profile',
      labelKey: 'nav.profile',
      icon: UserIcon
    }
  ]

  const adminLinks: NavLinkConfig[] = [
    {
      to: '/admin',
      labelKey: 'nav.dashboard',
      icon: HomeIcon
    },
    {
      to: '/admin/workers',
      labelKey: 'nav.workers',
      icon: UsersIcon,
      showBadge: pendingCount > 0
    },
    {
      to: '/admin/sessions',
      labelKey: 'nav.sessions',
      icon: ClockIcon
    },
    {
      to: '/admin/reports',
      labelKey: 'nav.reports',
      icon: DocumentIcon
    },
    {
      to: '/profile',
      labelKey: 'nav.more',
      icon: SettingsIcon
    }
  ]

  const links = isAdmin ? adminLinks : employeeLinks

  return (
    <div className="fixed bottom-4 left-4 right-4 z-40 pb-safe">
      <nav
        className="mx-auto max-w-md bg-white/80 dark:bg-gray-900/80 backdrop-blur-2xl rounded-full border border-gray-200/50 dark:border-gray-600/50 shadow-[0_8px_40px_rgba(0,0,0,0.15),0_2px_8px_rgba(0,0,0,0.1)] dark:shadow-[0_8px_40px_rgba(0,0,0,0.5)]"
        aria-label="Main navigation"
        role="navigation"
      >
        <div className="flex items-center justify-around h-16 px-3">
          {links.map((link) => {
            const isActive = location.pathname === link.to ||
              (link.to !== '/admin' && link.to !== '/dashboard' && location.pathname.startsWith(link.to))

            return (
              <NavLink
                key={link.to}
                to={link.to}
                className={`relative flex flex-col items-center justify-center flex-1 h-12 rounded-2xl transition-all duration-300 ${
                  isActive
                    ? 'text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/50 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100/80 dark:hover:bg-gray-800/50'
                }`}
                aria-current={isActive ? 'page' : undefined}
                aria-label={t(link.labelKey)}
              >
                <div className="relative">
                  <link.icon className="w-5 h-5" aria-hidden="true" />
                  {link.showBadge && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                  )}
                </div>
                <span className={`text-[10px] mt-0.5 font-semibold text-center w-full ${isActive ? 'text-primary-700 dark:text-primary-300' : ''}`}>
                  {t(link.labelKey)}
                </span>
              </NavLink>
            )
          })}
        </div>
      </nav>
    </div>
  )
})

// Icons
function HomeIcon({ className }: IconProps): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  )
}

function UserIcon({ className }: IconProps): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  )
}

function UsersIcon({ className }: IconProps): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  )
}

function ClockIcon({ className }: IconProps): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function SettingsIcon({ className }: IconProps): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  )
}

function DocumentIcon({ className }: IconProps): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  )
}

export default BottomNav
